import React, {useMemo} from 'react';
import clsx from 'clsx';
import {i18n} from '@netx/core/i18n';
import {SelectField} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {fileTypes} from '@netx/core-search/properties/search';
import {SearchSubTypeLabel} from '../../../../locales';
import {CommonRuleEditorProps} from '../../../types';
import {FileFormatType} from '../../constants';
import {getFileFormatTypeLabel} from '../../utils';
import {
	fileFormatRuleEditorClasses,
	getFileFormatRuleEditorUtilityClass,
	FileFormatRuleEditorClasses,
	FILE_FORMAT_RULE_EDITOR_CLASS_NAME,
} from './fileFormatRuleEditorClasses';

export type FileFormatRuleEditorOwnerState = WithClasses<
	FileFormatRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: FileFormatRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getFileFormatRuleEditorUtilityClass, classes);
};

const FileFormatRuleEditorRoot = styled('div', {
	name: FILE_FORMAT_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	'& > * + *': {
		marginLeft: `${theme.spacing(1)} !important`,
	},
}));

export type FileFormatRuleEditorProps = WithClasses<
	FileFormatRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: FileFormatType;
		elementSubType1Options: FileFormatType[];
		elementValue1: string;
		onSubType1Change: (subType1: FileFormatType) => void;
		onValue1Change: (value: string) => void;
	}
>;

const FileFormatRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementValue1,
	validationErrors,
	onSubType1Change,
	onValue1Change,
}: FileFormatRuleEditorProps) => {
	const ownerState: FileFormatRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const valueOptions = useMemo(() => {
		return Object.keys(fileTypes);
	}, []);

	return (
		<FileFormatRuleEditorRoot className={clsx(classes.root, className)}>
			<SelectField<FileFormatType, false, true, false>
				data-testid="file-format-rule-sub-type-1"
				disableClearable
				label={SearchSubTypeLabel.MATCH_TYPE}
				name={'rule-sub-type-1'}
				options={elementSubType1Options}
				value={elementSubType1}
				getOptionLabel={getFileFormatTypeLabel}
				onChange={(event, value) => onSubType1Change(value)}
			/>

			<SelectField<string, false, true, false>
				data-testid="file-format-rule-value-1"
				disableClearable
				error={validationErrors?.elementValue1?.message}
				label={i18n.t('i18n.fileFormat')}
				name={'rule-value-1'}
				options={valueOptions}
				value={elementValue1}
				onChange={(event, value) => onValue1Change(value)}
			/>
		</FileFormatRuleEditorRoot>
	);
};

export const FileFormatRuleEditorPrimitive = FileFormatRuleEditorInner;

export const FileFormatRuleEditor = FileFormatRuleEditorPrimitive;

export default FileFormatRuleEditor;

export type FileFormatRuleEditorGenericProps = WithClasses<
	FileFormatRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

export const FileFormatRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: FileFormatRuleEditorGenericProps) => {
	return (
		<FileFormatRuleEditor {...props} elementValue1={elementValue1.toString()} />
	);
};

export const FileFormatRuleEditorGeneric = FileFormatRuleEditorGenericPrimitive;
